import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import get from 'lodash/get';
import React, { useMemo, useState } from 'react';
import ItemTable from '../../components/ItemTable';
import DataLoadingAndErrors from '../../fhg/components/DataLoadingAndErrors';
import FHGTypography from '../../fhg/components/Typography';
import Grid from '../../fhg/components/Grid';

const ZONE_ALL_WHERE_QUERY = gql`
   query getZonesAllWhere($operatorId: [Int]) {
      zones: zone_AllWhere(zoneSearch: { operatorId: $operatorId }) {
         id
         name
      }
   }
`;

const SEGMENT_ALL_WHERE_QUERY = gql`
   query getSegmentsAllWhere($operatorId: [Int], $zoneId: [Int]) {
      segments: segment_AllWhere(segmentSearch: { operatorId: $operatorId, zoneId: $zoneId }) {
         id
         job
         serviceLineNumber
         lengthFT
         pressureTestSegment
         diameter
      }
   }
`;

/**
 * Read-only tab showing the zones for an operator and, once a zone is selected, the segments within it.
 */
export default function ZoneSegmentsTable({ operatorId }) {
   const [selectedZoneId, setSelectedZoneId] = useState();

   const { data: zoneData, loading: zoneLoading, error: zoneError } = useQuery(ZONE_ALL_WHERE_QUERY, {
      variables: { operatorId: [operatorId] },
      skip: !operatorId,
   });

   const { data: segmentData, loading: segmentLoading, error: segmentError } = useQuery(SEGMENT_ALL_WHERE_QUERY, {
      variables: { operatorId: [operatorId], zoneId: [selectedZoneId] },
      skip: !operatorId || !selectedZoneId,
   });

   const zones = get(zoneData, 'zones') || [];
   const segments = get(segmentData, 'segments') || [];
   const selectedZone = zones.find(zone => zone.id === selectedZoneId);

   const zoneColumns = useMemo(() => ([
      { Header: 'Name', accessor: 'name' },
   ]), []);

   const segmentColumns = useMemo(() => ([
      { Header: 'Job', accessor: 'job' },
      { Header: 'Service Line Number', accessor: 'serviceLineNumber' },
      { Header: 'Length (FT)', accessor: 'lengthFT' },
      { Header: 'Pressure Test Segment', accessor: 'pressureTestSegment' },
      { Header: 'Diameter', accessor: 'diameter' },
   ]), []);

   const handleSelectZone = (zone) => {
      setSelectedZoneId(zone?.id);
   };

   return (
      <Grid container spacing={4}>
         <Grid item xs={12} md={5}>
            <DataLoadingAndErrors error={zoneError} isLoading={zoneLoading} />
            <FHGTypography variant='h6' style={{ marginBottom: 8 }}>
               {`Zones (${zones.length})`}
            </FHGTypography>
            <ItemTable columns={zoneColumns} items={zones}
               onSelect={handleSelectZone}
               selectId={selectedZoneId}
               showSearchTextField={false} />
         </Grid>
         <Grid item xs={12} md={7}>
            <DataLoadingAndErrors error={segmentError} isLoading={segmentLoading} />
            <FHGTypography variant='h6' style={{ marginBottom: 8 }}>
               {selectedZone ? `Segments - ${selectedZone.name} (${segments.length})` : 'Segments'}
            </FHGTypography>
            {!selectedZoneId ? (
               <FHGTypography color='textSecondary'>
                  Select a zone to view its segments.
               </FHGTypography>
            ) : (
               <ItemTable columns={segmentColumns} items={segments} showSearchTextField={false} />
            )}
         </Grid>
      </Grid>
   );
}
